import React, { useCallback, useMemo } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import styled from "styled-components";

import { getLegendItemSize } from "components/screenshot/utils";

// Utility function to add break opportunities after special characters
const addBreakOpportunities = (text: string): string => {
  // Insert zero-width space after underscores, hyphens, dots, slashes, etc.
  return text.replace(/([_\-./\\])/g, "$1\u200B");
};

// Legend usage type constants
export const LEGEND_USAGE_TYPES = {
  CHART: "chart",
  MAP_SCREENSHOT: "mapScreenshot",
  FOCUS_PANEL: "focusPanel",
  DEFAULT: "default"
} as const;

// Padding configuration object map
export const LEGEND_PADDING_MAP = {
  [LEGEND_USAGE_TYPES.CHART]: "4px 12px",
  [LEGEND_USAGE_TYPES.MAP_SCREENSHOT]: "3px 12px",
  [LEGEND_USAGE_TYPES.FOCUS_PANEL]: "4px 12px",
  [LEGEND_USAGE_TYPES.DEFAULT]: "4px 12px"
} as const;

export type LegendUsageType =
  (typeof LEGEND_USAGE_TYPES)[keyof typeof LEGEND_USAGE_TYPES];

export type LegendItemData = {
  value: string;
  title: string;
  color: string;
  count?: number;
  id?: string;
};

export interface LegendRowProps<T extends LegendItemData = LegendItemData> {
  legend: T;
  useCount: boolean;
  showTypeWellRescat?: boolean;
  isWrappingText?: boolean;
  itemStyle?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  colorPickerStyle?: React.CSSProperties;
}

export type VirtualizedLegendListProps<T extends LegendItemData = LegendItemData> = {
  containerRef?: React.RefObject<HTMLDivElement>;
  legendUsage?: LegendUsageType;
  isWrappingText?: boolean;
  fontSize: number;
  height: number;
  itemRenderer: React.ComponentType<LegendRowProps<T>>;
  items: T[];
  showCounts: boolean;
  showTypeWellRescat?: boolean;
  virtuosoRef?: React.Ref<VirtuosoHandle>;
  width: string | number;
};

const VirtualizedLegendList = <T extends LegendItemData = LegendItemData>({
  items,
  width,
  height,
  showCounts,
  showTypeWellRescat = true,
  isWrappingText = false,
  fontSize,
  legendUsage = LEGEND_USAGE_TYPES.DEFAULT,
  containerRef,
  itemRenderer: ItemComponent,
  virtuosoRef
}: VirtualizedLegendListProps<T>) => {
  const baseItemHeight = getLegendItemSize(false, fontSize);

  // Determine padding: use legendUsage from map, fallback to default
  const paddingToUse =
    LEGEND_PADDING_MAP[legendUsage] || LEGEND_PADDING_MAP[LEGEND_USAGE_TYPES.DEFAULT];

  // Memoize processed items to avoid reprocessing on every render
  const processedItems = useMemo(() => {
    if (!isWrappingText) return items;

    return items.map((item) => ({
      ...item,
      title: addBreakOpportunities(item.title)
    }));
  }, [items, isWrappingText]);

  const ItemRenderer = useCallback(
    (index: number) => {
      const item = processedItems[index];
      if (!item) return null;

      return (
        <ItemComponent
          useCount={showCounts}
          showTypeWellRescat={showTypeWellRescat}
          legend={item}
          isWrappingText={isWrappingText}
          itemStyle={{
            padding: paddingToUse
          }}
          titleStyle={{
            fontSize: `${fontSize / 10}rem`,
            // Line height based on legend usage context
            lineHeight: "normal",
            minWidth: 0,
            whiteSpace: isWrappingText ? "normal" : "nowrap",
            textOverflow: isWrappingText ? "unset" : "ellipsis",
            overflow: isWrappingText ? "visible" : "hidden",
            hyphens: isWrappingText ? "auto" : "none",
            wordWrap: isWrappingText ? "break-word" : "normal",
            overflowWrap: isWrappingText ? "break-word" : "normal",
            wordBreak: isWrappingText ? "normal" : "normal"
          }}
          colorPickerStyle={{
            width: `${fontSize + 4}px`,
            height: `${fontSize + 4}px`
          }}
        />
      );
    },
    [
      processedItems,
      showCounts,
      showTypeWellRescat,
      isWrappingText,
      ItemComponent,
      paddingToUse,
      fontSize
    ]
  );

  return (
    <VirtuosoContainer ref={containerRef}>
      <Virtuoso
        defaultItemHeight={baseItemHeight}
        itemContent={ItemRenderer}
        overscan={5}
        ref={virtuosoRef}
        style={{ width, height }}
        totalCount={processedItems.length}
      />
    </VirtuosoContainer>
  );
};

export default VirtualizedLegendList;

const VirtuosoContainer = styled.div`
  width: 100%;
  height: 100%;
`;
